import type { UserTocLesson, UserTocUnit } from '../../../../types/userCoursesToc';

export type UnitCheckState = 'checked' | 'indeterminate' | 'unchecked';

/** Id of the dialog's help text, which explains why a unit or lesson cannot be selected. */
export const UNAVAILABLE_HELP_ID = 'lesson-selection-dialog-unavailable-help';

export type LessonTreeLesson = {
  /** Id of the "no AI" tag text, for the checkbox's `aria-describedby`; only on disabled lessons. */
  describedById: string | undefined;
  isDisabled: boolean;
  isSelected: boolean;
  lesson: UserTocLesson;
};

export type LessonTreeUnit = {
  checkState: UnitCheckState;
  contentsCount: number;
  /**
   * The "no lesson with AI" subtitle when the whole unit has nothing selectable; the help text when only
   * the search leaves nothing selectable in it; otherwise none.
   */
  describedById: string | undefined;
  /** Computed on the whole unit, never on the search-filtered one. */
  hasNoAvailableLessons: boolean;
  /** The unit checkbox acts on the visible lessons, so it is disabled when none of them is selectable. */
  isDisabled: boolean;
  isExpandable: boolean;
  isModulesOnly: boolean;
  lessons: LessonTreeLesson[];
  /** Id for the "no lesson with AI" counter; kept apart from `describedById`, which may be the help text's id. */
  noAiCounterId: string | undefined;
  selectedCount: number;
  /** Includes the disabled lessons: "0 OF 5" with one of them disabled. */
  totalCount: number;
  /** The visible unit (search-filtered lessons), which is what the unit toggle acts on. */
  unit: UserTocUnit;
};

type BuildLessonTreeParams = {
  selectedIds: ReadonlySet<string>;
  units: UserTocUnit[];
  /** Units left by the search, with their lessons filtered; defaults to `units`. */
  visibleUnits?: UserTocUnit[];
};

// The TOC endpoint only lists ingested content, so an empty `contents` means nothing for the AI to read.
export function isLessonAvailable(lesson: UserTocLesson): boolean {
  return (lesson.contents?.length ?? 0) > 0;
}

// Named apart from core/chatbot/utils/isModulesOnlyUnit, which reads another TOC (kcCount instead of contents).
export function isModulesOnlyTocUnit(unit: UserTocUnit): boolean {
  return (unit.lessons?.length ?? 0) === 0 && (unit.contents?.length ?? 0) > 0;
}

/** Ids the unit contributes to the selection: its available lessons, or its own id when modules-only. */
export function getSelectableIds(unit: UserTocUnit): string[] {
  if (isModulesOnlyTocUnit(unit)) return [unit.id];
  return (unit.lessons ?? []).filter(isLessonAvailable).map((lesson) => lesson.id);
}

/**
 * The selection minus anything not selectable in `units`: a preselected lesson that is no longer
 * available, or an id from another course, neither counts nor is emitted. Derived instead of pruning
 * the state because the dialog's open effect runs before the parent has passed the units.
 */
export function getEffectiveSelection(selectedIds: ReadonlySet<string>, units: UserTocUnit[]): Set<string> {
  const selectable = new Set(units.flatMap(getSelectableIds));
  return new Set(Array.from(selectedIds).filter((id) => selectable.has(id)));
}

/** Returns the same set when the lesson is not available, so a state update is a no-op. */
export function toggleLessonSelection(selectedIds: ReadonlySet<string>, lesson: UserTocLesson): ReadonlySet<string> {
  if (!isLessonAvailable(lesson)) return selectedIds;

  const next = new Set(selectedIds);
  if (next.has(lesson.id)) {
    next.delete(lesson.id);
  } else {
    next.add(lesson.id);
  }
  return next;
}

/** Selects every selectable id of the unit or, when all already are, deselects them. */
export function toggleUnitSelection(selectedIds: ReadonlySet<string>, unit: UserTocUnit): ReadonlySet<string> {
  const ids = getSelectableIds(unit);
  if (ids.length === 0) return selectedIds;

  const isAllSelected = ids.every((id) => selectedIds.has(id));
  const next = new Set(selectedIds);
  ids.forEach((id) => (isAllSelected ? next.delete(id) : next.add(id)));
  return next;
}

function getCheckState(selectableIds: string[], selectedIds: ReadonlySet<string>): UnitCheckState {
  const selectedCount = selectableIds.filter((id) => selectedIds.has(id)).length;
  if (selectedCount === 0) return 'unchecked';
  return selectedCount === selectableIds.length ? 'checked' : 'indeterminate';
}

function buildLessonNode(lesson: UserTocLesson, selectedIds: ReadonlySet<string>): LessonTreeLesson {
  const isDisabled = !isLessonAvailable(lesson);
  return {
    describedById: isDisabled ? `lesson-selection-dialog-no-ai-lesson-${lesson.id}` : undefined,
    isDisabled,
    isSelected: !isDisabled && selectedIds.has(lesson.id),
    lesson,
  };
}

function getUnitDescribedById(noAiCounterId: string | undefined, isDisabled: boolean): string | undefined {
  if (noAiCounterId) return noAiCounterId;
  return isDisabled ? UNAVAILABLE_HELP_ID : undefined;
}

function buildUnitNode(visibleUnit: UserTocUnit, fullUnit: UserTocUnit, selectedIds: ReadonlySet<string>): LessonTreeUnit {
  const visibleSelectableIds = getSelectableIds(visibleUnit);
  const hasNoAvailableLessons = getSelectableIds(fullUnit).length === 0;
  const isDisabled = visibleSelectableIds.length === 0;
  const noAiCounterId = hasNoAvailableLessons ? `lesson-selection-dialog-no-ai-unit-${fullUnit.id}` : undefined;
  const lessons = (visibleUnit.lessons ?? []).map((lesson) => buildLessonNode(lesson, selectedIds));

  return {
    checkState: getCheckState(visibleSelectableIds, selectedIds),
    contentsCount: fullUnit.contents?.length ?? 0,
    describedById: getUnitDescribedById(noAiCounterId, isDisabled),
    hasNoAvailableLessons,
    isDisabled,
    isExpandable: (fullUnit.lessons?.length ?? 0) > 0,
    isModulesOnly: isModulesOnlyTocUnit(fullUnit),
    lessons,
    noAiCounterId,
    selectedCount: lessons.filter((node) => node.isSelected).length,
    totalCount: lessons.length,
    unit: visibleUnit,
  };
}

/** View model of the unit/lesson tree: what is disabled, checked and counted, per visible unit. */
export function buildLessonTree({ selectedIds, units, visibleUnits = units }: BuildLessonTreeParams): LessonTreeUnit[] {
  const unitsById = new Map(units.map((unit) => [unit.id, unit]));
  return visibleUnits.map((visibleUnit) => buildUnitNode(visibleUnit, unitsById.get(visibleUnit.id) ?? visibleUnit, selectedIds));
}
